import {
    ChangeDetectionStrategy,
    Component,
    LOCALE_ID,
    type ResourceRef,
    Signal,
    WritableSignal,
    computed,
    inject,
    resource,
    signal,
} from '@angular/core';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { RtButtonDirective } from '@rt-tools/ui-kit-v2';
import { RtSkeletonWrapperComponent } from '@rt-tools/ui-kit-v2/skeleton-wrapper';
import { BlogArticlesApiService, IBlogArticle, blogCardsOf } from '@stair-craft/blog/data-access';
import { BlogArticleCardComponent } from '@stair-craft/blog/ui';
import { PageHeaderComponent } from '@stair-craft/common/components';

const BEM_BLOCK: string = 'sc-blog-list-page';

// Placeholder cards while the articles load: two rows of the three-column grid.
const SKELETON_SLOTS: readonly number[] = [1, 2, 3, 4, 5, 6];

@Component({
    selector: 'sc-blog-list-page',
    templateUrl: './blog-list-page.component.html',
    styleUrl: './blog-list-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        RtButtonDirective,
        RtSkeletonWrapperComponent,

        // components
        BlogArticleCardComponent,
        PageHeaderComponent,
    ],
    host: {
        class: BEM_BLOCK,
    },
})
export default class BlogListPageComponent {
    readonly #localeId: string = inject(LOCALE_ID);
    readonly #api: BlogArticlesApiService = inject(BlogArticlesApiService);

    // Опубликованные статьи CMS; если сервер CMS не ответил, список пуст.
    readonly #stored: ResourceRef<readonly IBlogArticle.State[] | undefined> = resource({
        loader: (): Promise<readonly IBlogArticle.State[]> => this.#api.published(this.#localeId),
    });

    readonly #tags: ResourceRef<readonly IBlogArticle.Tag[] | undefined> = resource({
        loader: (): Promise<readonly IBlogArticle.Tag[]> => this.#api.tags(),
    });

    protected readonly skeletonSlots: readonly number[] = SKELETON_SLOTS;

    // On navigation inside the site the articles arrive after the page: placeholders hold their place
    // instead of the empty-list text.
    protected readonly loading: Signal<boolean> = computed(() => this.#stored.isLoading());

    protected readonly cards: Signal<readonly IBlogArticle.Card[]> = computed(() =>
        blogCardsOf(this.#stored.value() ?? [], this.#tags.value() ?? [])
    );

    // Рубрики фильтра — подписи рубрик статей списка в порядке первого появления.
    protected readonly rubrics: Signal<readonly string[]> = computed(() => [
        ...new Set(
            this.cards()
                .map((card: IBlogArticle.Card) => card.rubric)
                .filter((rubric: string) => rubric !== '')
        ),
    ]);

    // `null` = no active filter, every article is shown.
    protected readonly activeRubric: WritableSignal<string | null> = signal<string | null>(null);

    protected readonly filteredCards: Signal<readonly IBlogArticle.Card[]> = computed((): readonly IBlogArticle.Card[] => {
        const rubric: string | null = this.activeRubric();
        const cards: readonly IBlogArticle.Card[] = this.cards();
        return rubric === null ? cards : cards.filter((card: IBlogArticle.Card): boolean => card.rubric === rubric);
    });

    protected setFilter(rubric: string | null): void {
        this.activeRubric.set(rubric);
    }
}
